import React, {FC} from "react";
import {useEditorLocation} from "../editor/editor.hook";
import {useHistory} from "react-router-dom";
import {Form, Formik} from "formik";
import clsx from "clsx";
import {CircularProgress, IconButton, makeStyles, TypographyProps} from "@material-ui/core";
import * as yup from "yup";
import {FormikDateSelect} from "./FormikDateSelect";
import {DateDisplay} from "./DateDisplay";
import {RedCloseIcon} from "./RedCloseIcon";
import {GreenCheckIcon} from "./GreenCheckIcon";

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        alignItems: 'center'
    }
}));

interface DateFormProps {
    label: string;
    required?: boolean;
    initialValue?: number | null;
    className?: string;
    onSubmit: (date: number) => Promise<any>;
    moreThan?: number;
    disablePast?: boolean;
}

const DateForm: FC<DateFormProps> = (props) => {
    const classes = useStyles();
    const history = useHistory();
    const [isLoading, setIsLoading] = React.useState(false);

    const closeForm = () => history.replace({search: undefined});

    return (
        <Formik
            initialValues={{value: props.initialValue ?? 0}}
            onSubmit={(values) => {
                setIsLoading(true);
                props.onSubmit(values.value)
                    .catch(() => {
                    })
                    .finally(() => {
                        setIsLoading(false);
                        closeForm();
                    })
            }}
            validationSchema={
                props.moreThan
                    ? yup.object({value: yup.number().moreThan(props.moreThan, "Muss später sein")})
                    : yup.object({value: props.required ? yup.number().required('benötigt') : yup.number()})
            }
        >
            {({submitForm}) => (
                <Form className={clsx(props.className, classes.container)}>
                    <FormikDateSelect name="value" label={props.label} disablePast={props.disablePast}/>
                    {isLoading
                        ? <CircularProgress size={20} color="secondary"/>
                        : (
                            <>
                                <IconButton edge="end" onClick={closeForm}>
                                    <RedCloseIcon/>
                                </IconButton>
                                <IconButton edge="end" onClick={submitForm}>
                                    <GreenCheckIcon/>
                                </IconButton>
                            </>
                        )
                    }
                </Form>
            )}
        </Formik>
    )
}

interface EditableDateProps {
    onSubmit: (date: number) => Promise<any>;
    value?: number | null;
    className?: string;
    editorIdentifier: string;
    label: string;
    required?: boolean;
    typographyProps?: TypographyProps;
    dateFormat?: string
    moreThan?: number;
    disablePast?: boolean;
}

export const EditableDate: FC<EditableDateProps> = (props) => {

    const {mode, reference} = useEditorLocation();
    const showDateForm = mode === 'edit' && reference === props.editorIdentifier;

    return showDateForm
        ? (
            <DateForm
                label={props.label}
                onSubmit={props.onSubmit}
                className={props.className}
                required={props.required}
                initialValue={props.value}
                moreThan={props.moreThan}
                disablePast={props.disablePast}
            />
        )
        : (
            <DateDisplay
                editIdentifier={props.editorIdentifier}
                label={props.label}
                required={props.required}
                className={props.className}
                date={props.value}
                dateFormat={props.dateFormat}
                typographyProps={props.typographyProps}
            />
        )
}
